import { useEffect } from 'react';
import { presenceActions } from '@/features/presence/presenceStore';
import { useSelfPresence } from '@/features/presence/hooks/usePresence';
import type { ActivityState, ManualState, PresenceSnapshot } from '@/features/presence/types';
import { withDesktop } from '../bridge';
import { useDesktopEvent } from './useDesktopEvent';
import type { DesktopPresenceState, TrayPresence } from '../types';

/**
 * THE COLLAPSE ONTO THE FIVE SETTABLE STATES.
 *
 * This is a lossy map and it has to be: `TrayPresence` is the tray's RADIO
 * MENU, so every member of it is something a user may pick, and six of the
 * states below are not — In a call, In a meeting, Presenting and Screen sharing
 * are derived by the server from `call_participants` (infra/sql/062, "Never
 * client-set"), Be right back is folded into Away because the tray has no
 * separate glyph for it, and Out of office is not a choice either.
 *
 * WHAT MUST NOT HAPPEN is this being the ONLY thing the shell is told, which is
 * what it was: the desktop then literally could not paint "In a call" — the
 * fact had already been destroyed in this file before it crossed the bridge.
 * `displayState` below carries the undamaged value alongside it.
 */
const TO_TRAY: Readonly<Record<ActivityState, TrayPresence>> = {
  available: 'available',
  busy: 'busy',
  inAMeeting: 'busy',
  inACall: 'busy',
  presenting: 'busy',
  screenSharing: 'busy',
  away: 'away',
  beRightBack: 'away',
  doNotDisturb: 'doNotDisturb',
  /* WRONG, AND KNOWINGLY LEFT: an out-of-office user is not offline. The tray
     has five glyphs and none of them is purple, so this is the least bad of
     five bad answers for the ICON. The badge no longer depends on it. */
  outOfOffice: 'offline',
  offline: 'offline',
};

/*
 * NO `available` HERE. The tray has no "Reset status" row, so its "Available"
 * was the only way back from Busy or Do not disturb - and it stored a manual
 * override that pinned the person green through every idle hour for a week.
 * From the tray, Available now means what it means in Teams: back to automatic.
 */
const TO_MANUAL: Readonly<Record<Exclude<TrayPresence, 'available'>, ManualState>> = {
  busy: 'appearBusy',
  doNotDisturb: 'appearDnd',
  away: 'appearAway',
  offline: 'appearOffline',
};

/**
 * WHAT THE PERSON IS ACTUALLY DOING, undamaged.
 *
 * `effectiveState` is what every colleague sees — the server resolves manual
 * override, out-of-office, the call/meeting activity tier and the connection
 * count together and publishes the answer, and `applyDeltas` writes it straight
 * into `self`. One distinction it cannot carry: the resolver collapses the
 * `appearOffline` override onto plain `offline` (infra/sql/068 and its client
 * mirror), and the two are the same grey dot with the same X on purpose. Only
 * the WORD differs, and a word is exactly what an accessible description is,
 * so the override is recovered from the declaration it came from.
 */
function displayState(self: PresenceSnapshot): DesktopPresenceState {
  if (self.effectiveState === 'offline' && self.manualState === 'appearOffline') {
    return 'appearOffline';
  }
  return self.effectiveState;
}

/**
 * Keeps the tray's PRESENCE honest and puts the real one on the Windows
 * taskbar: the icon carries the same state the roster shows, and a presence
 * chosen FROM the tray is a manual override exactly as if it had been picked in
 * the app.
 *
 * TWO CALLS, ONE FACT, AND THE ORDER MATTERS. The shell and the workspace ship
 * apart, so this bundle regularly runs inside an older shell whose presence
 * handler only understands a bare `TrayPresence` string and silently ignores an
 * object. Sending the string first keeps such a shell's tray moving; sending
 * the report second is what a current shell paints the taskbar badge from. A
 * current shell ignores the string once it has seen a report.
 *
 * NOTHING IS SENT UNTIL THE SNAPSHOT IS RESOLVED. The store seeds itself at
 * Available so the chip can render immediately, and that seed is a guess — the
 * app itself refuses to draw an unresolved snapshot ("Unresolved snapshots now
 * render nothing rather than a lie"). A taskbar badge is not a smaller claim
 * than a chip, so `display` is omitted until the server has answered, and the
 * shell paints no overlay rather than a green dot that may be wrong. The tray,
 * whose behaviour predates this and whose default is its own kind of guess, is
 * left exactly as it was.
 *
 * The MEDIA half of this used to live here too, and that was wrong once a call
 * ran in its own window: this hook belongs to the workspace (presence has one
 * heartbeat per person, not one per window), so the shell never heard about
 * media held anywhere else and the tray's Mute item sat greyed out during a
 * call. Reporting media is `useDesktopMediaState`, which BOTH documents mount.
 */
export function useDesktopPresence(): void {
  /* The whole snapshot, not `useSelfPresenceState()`: the badge needs the
     activity, whether it is resolved, and which manual override produced it.
     It re-renders as often as this user's own presence changes — the same rate
     as the presence chip in the top bar — and this component renders null. */
  const self = useSelfPresence();
  const tray = TO_TRAY[self.effectiveState];
  const display = self.resolved ? displayState(self) : undefined;

  useEffect(() => {
    withDesktop((bridge) => {
      void bridge.setPresence(tray);
      return bridge.setPresence({ tray, display });
    });
  }, [tray, display]);

  useDesktopEvent('presence-request', (event) => {
    if (event.state === 'available') presenceActions.setManualState(null);
    else presenceActions.setManualState(TO_MANUAL[event.state], 'never');
  });
}
